import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/api/client'
import { useT } from '@/i18n'
import { commonMessages } from '@/i18n/messages/common'
import { usersMessages } from '@/i18n/messages/users'
import { useWfT } from '@/features/workflow/shared'
import { Field, PAGES } from './EditUserDialog'
import { ConfirmButton, ErrorLine, Modal, btnPrimary, btnSecondary, inputCls, labelCls } from './parts'
import { roleName, type AccessRole } from './types'

/** The role catalogue: which modules each role opens. Admins change it; user managers read it. */
export function RolesTab({ roles, meIsAdmin }: { roles: AccessRole[]; meIsAdmin: boolean }) {
  const t = useT(usersMessages)
  const tw = useWfT()
  const tc = useT(commonMessages)
  const [editing, setEditing] = useState<AccessRole | 'new' | null>(null)
  const pageLabel = (k: string) => t(PAGES.find((p) => p.key === k)?.label ?? 'unknown')

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-prose text-sm text-neutral-500">{t('rolesIntro')} {meIsAdmin ? t('roleChangesLive') : t('adminOnlyRoles')}</p>
        {meIsAdmin && <button onClick={() => setEditing('new')} className={btnPrimary}>{t('newRole')}</button>}
      </div>
      <div className="overflow-x-auto rounded-2xl bg-white ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-200 text-left text-xs text-neutral-500 dark:border-neutral-800">
              <th className="px-3 py-2 font-medium">{t('colRole')}</th>
              <th className="px-3 py-2 font-medium">{t('colModules')}</th>
              <th className="px-3 py-2 font-medium">{t('colCanEdit')}</th>
              <th className="px-3 py-2 text-right font-medium">{t('colHolders')}</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {roles.map((r) => (
              <tr key={r.role} className="border-b border-neutral-100 align-top last:border-0 dark:border-neutral-800/60">
                <td className="px-3 py-2">
                  <div className="font-medium">{roleName(tw, r.role, roles)}</div>
                  <div className="text-xs text-neutral-400">{r.builtin ? t('builtin') : r.role}{r.description ? ` · ${r.description}` : ''}</div>
                </td>
                <td className="px-3 py-2">
                  {r.pages.length === 0 ? <span className="text-xs text-neutral-400">{t('noModules')}</span> : (
                    <div className="flex flex-wrap gap-1">
                      {r.pages.map((p) => <span key={p} className="rounded bg-neutral-100 px-1.5 py-0.5 text-[11px] text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">{pageLabel(p)}</span>)}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2">{r.canEdit ? <span className="text-green-600">✓</span> : <span className="text-neutral-300 dark:text-neutral-600">—</span>}</td>
                <td className="tabular px-3 py-2 text-right">{r.holders}</td>
                <td className="px-3 py-2 text-right">
                  {meIsAdmin && <button onClick={() => setEditing(r)} className="rounded-md px-2 py-1 text-xs font-medium text-brand-700 ring-1 ring-brand-600/30 hover:bg-brand-50 dark:text-brand-100 dark:ring-brand-100/20 dark:hover:bg-brand-700/20">{tc('edit')}</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <RoleDialog role={editing === 'new' ? null : editing} catalog={roles} onClose={() => setEditing(null)} />}
    </div>
  )
}

function RoleDialog({ role, catalog, onClose }: { role: AccessRole | null; catalog: AccessRole[]; onClose: () => void }) {
  const qc = useQueryClient()
  const t = useT(usersMessages)
  const tw = useWfT()
  const tc = useT(commonMessages)
  const [key, setKey] = useState(role?.role ?? '')
  const [label, setLabel] = useState(role ? roleName(tw, role.role, catalog) : '')
  const [description, setDescription] = useState(role?.description ?? '')
  const [pages, setPages] = useState<Set<string>>(new Set(role?.pages ?? []))
  const [canEdit, setCanEdit] = useState(role?.canEdit ?? false)
  const [error, setError] = useState<string | null>(null)
  const done = () => { void qc.invalidateQueries({ queryKey: ['user-roles'] }); void qc.invalidateQueries({ queryKey: ['users'] }); onClose() }
  const fail = (e: unknown) => setError(e instanceof ApiError ? e.message : t('couldNotSave'))

  const save = useMutation({
    mutationFn: () => api.put(`/api/users/roles/${encodeURIComponent(key.trim().toLowerCase())}`,
      { label: role?.builtin ? role.label : label, description: description || null, pages: [...pages], canEdit }),
    onSuccess: done, onError: fail,
  })
  const remove = useMutation({ mutationFn: () => api.del(`/api/users/roles/${encodeURIComponent(role!.role)}`), onSuccess: done, onError: fail })

  return (
    <Modal onClose={onClose} title={role ? t('editRole') : t('newRole')}
      footer={<>
        <ErrorLine error={error} />
        <div className="flex gap-2">
          <button onClick={onClose} className={btnSecondary}>{tc('cancel')}</button>
          <button onClick={() => { setError(null); save.mutate() }} disabled={save.isPending || !key.trim() || !label.trim()} className={btnPrimary}>
            {save.isPending ? t('saving') : tc('save')}
          </button>
        </div>
      </>}>
      {!role && <Field label={t('roleKey')} hint={t('roleKeyHint')}>
        <input className={`${inputCls} font-mono`} value={key} autoCapitalize="none" spellCheck={false} onChange={(e) => setKey(e.target.value.toLowerCase())} />
      </Field>}
      <Field label={t('roleName')}>
        <input className={inputCls} value={label} disabled={role?.builtin} onChange={(e) => setLabel(e.target.value)} />
      </Field>
      <Field label={t('roleDescription')}>
        <input className={inputCls} value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <div>
        <span className={labelCls}>{t('colModules')}</span>
        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {PAGES.filter((p) => p.key !== 'users').map((p) => (
            <label key={p.key} className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm ring-1 ring-neutral-200 dark:ring-neutral-700">
              <input type="checkbox" checked={pages.has(p.key)} className="rounded border-neutral-300 text-brand-600"
                     onChange={() => setPages((prev) => { const n = new Set(prev); if (n.has(p.key)) n.delete(p.key); else n.add(p.key); return n })} />
              {t(p.label)}
            </label>
          ))}
        </div>
        <p className="mt-1 text-xs text-neutral-400">{t('usersPageNotByRole')}</p>
      </div>
      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" checked={canEdit} onChange={(e) => setCanEdit(e.target.checked)} className="mt-0.5 rounded border-neutral-300 text-brand-600" />
        <span><span className="font-medium">{t('roleCanEdit')}</span><span className="block text-xs text-neutral-500">{t('canEditHint')}</span></span>
      </label>
      {role && !role.builtin && (
        <ConfirmButton danger label={t('deleteRole')} question={t('deleteRoleConfirm', { name: role.label })} busy={remove.isPending} onConfirm={() => remove.mutate()} />
      )}
    </Modal>
  )
}
